const React = require('react');
const T = require('prop-types');
const { default: styled, css } = require('styled-components');
const { default: MuiAutocomplete, autocompleteClasses } = require('@mui/material/Autocomplete');
const { default: TextField } = require('@mui/material/TextField');
const { default: MuiMenuItem } = require('@mui/material/MenuItem');
const { default: Classes } = require('./styles.scss');
const UniqueId = require('lodash/uniqueId');
const { transient$Props } = require('utils/styles');
const ClearFieldAdornment = require('hooks/useFormPrimitives/ClearFieldAdornment');
const DropdownOptionStyles = require('hooks/useFormPrimitives/dropdownOptionStyles');
const {
    singleLineFieldStyles,
    SINGLE_LINE_INPUT_HEIGHT
} = require('hooks/useFormPrimitives/singleLineFieldStyles');

const {
    useRef
} = React;

const internals = {};

module.exports = function MaterialReactAutocomplete(props) {

    const {
        id: _id,
        onChange,
        onTextChange,
        value,
        searchText,
        dataSource,
        dataSourceConfig,
        style,
        ...inputProps
    } = props;

    const {
        Autocomplete,
        renderInput,
        sortByStartsWith
    } = internals;

    const id = useRef(_id || UniqueId('MaterialReactAutocomplete'));
    const hasClearValue = !!(searchText && String(searchText).length > 0);
    const clearDisabled = !!inputProps.disabled || !hasClearValue;

    const getOptionLabel = (option) => {

        if (!option) {
            return '';
        }

        if (typeof option === 'string') {
            return option;
        }

        return option[dataSourceConfig.label] || '';
    };
    const isOptionEqualToValue = (option, optionValue) => internals.optionsAreEqual({
        getOptionLabel,
        option,
        value: optionValue
    });

    const renderOption = (optionProps, _option, state) => {

        const { Option } = internals;

        return (
            <Option component='li' {...optionProps}>
                {optionProps.key}
            </Option>
        );
    };

    const onInputChange = (_evt, newSearchVal, reason) => {

        if (reason === 'reset') {
            return;
        }

        if (newSearchVal === '') {
            inputProps.handleClearField?.();
            return;
        }

        onTextChange(newSearchVal);
    };

    const onSelectOption = (_event, _value, _reason, _details) => {

        if (_reason === 'clear') {
            inputProps.handleClearField?.();
            return;
        }

        onTextChange(getOptionLabel(_value));
        onChange(_value);
    };

    const options = sortByStartsWith(dataSource, searchText, getOptionLabel);

    return (
        <Autocomplete
            id={id.current}
            style={style}
            open={!value && !!searchText}
            className='fullWidth'
            disableClearable
            classes={{
                popper: Classes.popper
            }}
            $variant={inputProps.variant}
            value={value}
            inputValue={searchText}
            forcePopupIcon={false}
            ListboxProps={{
                style: {
                    maxHeight: 200,
                    overflowY: 'auto'
                }
            }}
            onInputChange={onInputChange}
            onChange={onSelectOption}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={renderOption}
            renderInput={(renderInputProps) => {

                return renderInput({
                    ...renderInputProps,
                    ...inputProps,
                    value: searchText,
                    onTextChange,
                    // Ensure keystrokes always propagate, even if Autocomplete's onInputChange
                    // doesn't fire for some reason (controlled value/inputValue edge cases).
                    inputProps: {
                        ...renderInputProps.inputProps,
                        ...(inputProps.inputProps || {}),
                        onChange: (event) => {
                            renderInputProps.inputProps?.onChange?.(event);
                            onTextChange(event.target.value);
                        }
                    },
                    clearDisabled,
                    onBlur: () => {

                        if (!value) {
                            // inputProps.handleClearField?.();
                        }
                    }
                });
            }}
        />
    );
};

module.exports.propTypes = {
    id: T.any,
    dataSource: T.array.isRequired,
    dataSourceConfig: T.shape({
        label: T.string,
        id: T.string
    }),
    style: T.object,
    onChange: T.func,
    onTextChange: T.func,
    searchText: T.string,
    value: T.any,
    className: T.string,
    listStyle: T.string,
    handleClearField: T.func
};

internals.renderInput = (props) => {

    const {
        value,
        inputStyle,
        handleClearField,
        clearDisabled,
        onTextChange,
        inputProps,
        InputProps,
        InputLabelProps,
        inputRef,
        label,
        fieldStyles,
        ...rest
    } = props;

    const { SingleLineTextField } = internals;

    return (
        <SingleLineTextField
            {...rest}
            label={label}
            inputRef={inputRef}
            inputProps={{
                ...inputProps,
                autoComplete: 'new-password'
            }}
            value={value}
            fullWidth
            InputLabelProps={InputLabelProps}
            InputProps={{
                ...InputProps,
                endAdornment: <ClearFieldAdornment
                    disabled={!!clearDisabled}
                    onClear={() => {
                        handleClearField?.();
                        onTextChange?.('');
                    }}
                    existingEndAdornment={InputProps?.endAdornment}
                />
            }}
            inputStyle={{
                textOverflow: 'ellipsis',
                ...inputStyle
            }}
            $fieldStyles={fieldStyles}
        />
    );
};

internals.SingleLineTextField = styled(TextField)`
    ${singleLineFieldStyles}
    ${({ $fieldStyles }) => css`${$fieldStyles || ''}`}
`;

internals.Autocomplete = styled(MuiAutocomplete, transient$Props)`
    ${singleLineFieldStyles}

    input.MuiInput-input {
        // Idk why, but this field wants to be 1px taller than the rest.
        // So we gotta take it down a notch.
        height: ${SINGLE_LINE_INPUT_HEIGHT - 1}px !important;
        line-height: ${SINGLE_LINE_INPUT_HEIGHT - 1}px !important;
        padding-top: 4px !important;
        padding-bottom: 5px !important;
        box-sizing: content-box;
        min-width: 0;
    }

    /* Prevent adornments from wrapping to a new line in narrow layouts. */
    & .MuiAutocomplete-inputRoot,
    & .MuiInputBase-root {
        flex-wrap: nowrap !important;
    }
`;

internals.Option = styled(MuiMenuItem)`
    ${DropdownOptionStyles.option}
`;

internals.optionsAreEqual = ({
    getOptionLabel,
    option,
    value
}) => {

    if (!value) {
        return false;
    }

    return getOptionLabel(option) === getOptionLabel(value);
};

internals.sortByStartsWith = (arr = [], prefix = '', getOptionLabel) => {

    const options = Array.isArray(arr) ? arr : [];
    const normalizedPrefix = String(prefix || '').toLowerCase();

    return options.slice().sort((optionA, optionB) => {

        const labelA = getOptionLabel(optionA)?.toLowerCase();
        const labelB = getOptionLabel(optionB)?.toLowerCase();

        const aStartsWith = labelA?.startsWith(normalizedPrefix);
        const bStartsWith = labelB?.startsWith(normalizedPrefix);

        if (aStartsWith && !bStartsWith) {
            return -1;
        }

        if (!aStartsWith && bStartsWith) {
            return 1;
        }

        // Alphabetical secondary sort
        return labelA?.localeCompare(labelB);
    });
};

module.exports.internals = internals;
